'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import type { EventTranslation } from '@/lib/api/event/models';
import { useApiTranslation } from '@/lib/hooks/useApiTranslation';
import { Icon } from '@/lib/ui-kit/icons';
import { cn } from '@/lib/ui-kit/utils';

interface EventRulesProps {
  rule?: string;
  translations?: EventTranslation[];
}

/**
 * Resolution rules as a card: a heading row, the markdown text clamped to
 * three lines with a hard cut (no fade), and a "Read more" toggle that exists
 * only when the text actually overflows — so a short rule renders as a plain
 * card with no gap, and a long one never shows half a line.
 */
export const EventRules = ({ rule, translations }: EventRulesProps) => {
  const t = useTranslations('event.rules');
  const { tr } = useApiTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  const translatedRule = tr(translations, 'rule', rule ?? '');

  // Overflow is measured, not guessed from length: the clamp depends on the
  // column width, which differs between the mobile and desktop layouts.
  const measure = useCallback(() => {
    const node = bodyRef.current;
    if (!node) return;
    setIsOverflowing(node.scrollHeight > node.clientHeight + 1);
  }, []);

  useEffect(() => {
    if (isExpanded) return;
    measure();
    const node = bodyRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [measure, isExpanded, translatedRule]);

  const handleToggle = () => setIsExpanded((value) => !value);

  if (!translatedRule?.trim()) return null;

  const showToggle = isExpanded || isOverflowing;

  return (
    <section
      aria-labelledby="event-rules-heading"
      className="flex flex-col gap-3 rounded-[10px] bg-v2-surface-secondary px-6 py-5 shadow-v2-card"
    >
      <div className="flex items-center gap-2">
        <Icon name="Document" size={18} className="shrink-0 text-v2-icon-primary" />
        <h3
          id="event-rules-heading"
          className="text-[15px] font-semibold leading-5 text-v2-text-primary"
        >
          {t('title')}
        </h3>
      </div>

      <div
        ref={bodyRef}
        className={cn(
          'flex flex-col gap-2 text-[13px] leading-5 tracking-[-0.13px] text-v2-text-secondary',
          !isExpanded && 'line-clamp-3',
        )}
      >
        <Markdown
          // Editor-authored markdown starts at `#`, but the page already has
          // its <h1> (the event title) and this block is headed by an <h3>.
          // Demote every level so a rule heading never becomes a second <h1>.
          components={{
            h1: ({ children }) => (
              <h4 className="text-[14px] font-semibold text-v2-text-primary">{children}</h4>
            ),
            h2: ({ children }) => (
              <h5 className="text-[14px] font-semibold text-v2-text-primary">{children}</h5>
            ),
            h3: ({ children }) => (
              <h6 className="text-[13px] font-semibold text-v2-text-primary">{children}</h6>
            ),
            p: ({ children }) => <p>{children}</p>,
            ul: ({ children }) => <ul className="list-disc space-y-1 pl-5">{children}</ul>,
            ol: ({ children }) => <ol className="list-decimal space-y-1 pl-5">{children}</ol>,
            li: ({ children }) => <li>{children}</li>,
            a: ({ href, children }) => (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-v2-bg-blue hover:underline"
              >
                {children}
              </a>
            ),
            strong: ({ children }) => (
              <strong className="font-semibold text-v2-text-primary">{children}</strong>
            ),
            code: ({ children }) => (
              <code className="rounded bg-v2-btn-secondary px-1 py-0.5 text-[12px]">
                {children}
              </code>
            ),
            blockquote: ({ children }) => (
              <blockquote className="border-l-2 border-v2-bg-blue/50 pl-3 italic">
                {children}
              </blockquote>
            ),
            hr: () => <hr className="my-2 border-v2-border" />,
          }}
        >
          {translatedRule}
        </Markdown>
      </div>

      {showToggle && (
        <button
          type="button"
          onClick={handleToggle}
          aria-expanded={isExpanded}
          className="flex w-fit cursor-pointer items-center gap-1 text-[13px] leading-4 font-medium text-v2-text-primary transition-colors hover:text-v2-text-secondary"
        >
          {isExpanded ? t('showLess') : t('readMore')}
          <Icon
            name="ArrowSmallDown"
            size={16}
            className={cn('shrink-0 transition-transform', isExpanded && 'rotate-180')}
          />
        </button>
      )}
    </section>
  );
};
